/**
 * Onboarding Backend Selector — IN-3.b
 * ─────────────────────────────────────
 * Reads `VITE_ONBOARDING_BACKEND` and returns the matching OnboardingBackend.
 *
 * Default is now "supabase" (IN-3.b wired). Set VITE_ONBOARDING_BACKEND=local
 * to fall back to the localStorage store for local dev without Supabase.
 *
 * Hard rules:
 *   • Unknown values default to "supabase".
 *   • "local" returns LocalOnboardingStore for offline/dev use only.
 *   • Selector is pure — no side effects, no caching of an instance.
 *     Callers (provider) hold their own reference.
 */

import { LocalOnboardingStore } from "./LocalOnboardingStore";
import { SupabaseOnboardingStore } from "./SupabaseOnboardingStore";
import type { OnboardingBackend } from "./types";

export type OnboardingBackendKind = "local" | "supabase";

export function resolveOnboardingBackendKind(
  raw: string | undefined,
): OnboardingBackendKind {
  // "local" must be explicitly opted into; everything else (including
  // undefined / unknown values) defaults to supabase.
  return raw === "local" ? "local" : "supabase";
}

export function getOnboardingBackend(
  override?: OnboardingBackendKind,
): OnboardingBackend {
  const kind =
    override ??
    resolveOnboardingBackendKind(
      typeof import.meta !== "undefined"
        ? (import.meta.env?.VITE_ONBOARDING_BACKEND as string | undefined)
        : undefined,
    );
  return kind === "local"
    ? new LocalOnboardingStore()
    : new SupabaseOnboardingStore();
}

export { LocalOnboardingStore } from "./LocalOnboardingStore";
export {
  SupabaseOnboardingStore,
  SUPABASE_NOT_CONNECTED_MESSAGE,
} from "./SupabaseOnboardingStore";
export type { OnboardingBackend, OnboardingSnapshot } from "./types";
export {
  ONBOARDING_SNAPSHOT_VERSION,
  emptyOnboardingSnapshot,
} from "./types";
